import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTranslatedUI } from '../hooks/useTranslatedUI';
import { translateBatch } from '../services/translationService';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ContextualServices } from '../components/ContextualServices';
import { Button } from '../components/ui/button';
import { getSchemeApplyLink } from '../utils/schemeApplyLinks';
import {
  ArrowLeft, Sparkles, IndianRupee, AlertTriangle, MapPin, Send,
  CheckCircle2, Clock, ChevronDown, ChevronUp, Share2, ExternalLink,
  FileText, Loader2, MessageCircle, Newspaper, Navigation, ShieldAlert,
  CalendarClock, Footprints, CircleDollarSign, TrendingUp, ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RESULTS_STRINGS = {
  newAnalysis: "New Analysis",
  yourReport: "Your KMS Smart Report",
  share: "Share",
  personalizedInsight: "Personalized Insight",
  estimatedBenefit: "Estimated Total Potential Benefit",
  qualifyingSchemes: "Qualifying Schemes",
  headwinds: "Potential Headwinds",
  keyDeadlines: "Key Deadlines",
  nextSteps: "Your Next Steps",
  bring: "Bring:",
  nearbyCentres: "Nearby Service Centres",
  away: "away",
  relatedNews: "Related News",
  moreQuestions: "Have More Questions?",
  incomeIntelligence: "Income Intelligence",
  smartTip: "Smart Tip",
  seeFullPlan: "See full income plan",
  askFollowUp: "Ask Follow-up Questions",
  chatSubtitle: "Chat with our AI about your report",
  chatPlaceholder: "Ask anything about your KMS Smart report...",
  typePlaceholder: "Type your question...",
  thinking: "Thinking...",
  startNewAnalysis: "Start New Analysis",
  effortLevel: "Effort Level",
  deadline: "Deadline",
  whereToApply: "Where to Apply",
  howToApply: "How to Apply",
  documentsNeeded: "Documents Needed",
};

export default function ImpactResults() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { language } = useLanguage();
  const ui = useTranslatedUI(RESULTS_STRINGS);
  const chatEndRef = useRef(null);

  const analysisData = location.state?.analysisData;
  const profileText = location.state?.profileText || '';
  const selectedLanguage = location.state?.selectedLanguage || 'en';

  const [expandedScheme, setExpandedScheme] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [incomeBandData, setIncomeBandData] = useState(null);
  const [incomeBandVisible, setIncomeBandVisible] = useState(false);
  const [translatedContent, setTranslatedContent] = useState(null);

  useEffect(() => {
    if (!analysisData) {
      navigate('/impact-analyser');
    }
  }, [analysisData, navigate]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Classify user profile for income intelligence band
  useEffect(() => {
    if (!analysisData || !profileText) return;
    const classifyProfile = async () => {
      try {
        const res = await fetch(`${API_URL}/api/income-intelligence/classify-search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: profileText, language: selectedLanguage }),
        });
        const data = await res.json();
        if (data.show_band && data.income_band) {
          setIncomeBandData(data.income_band);
          // Slide in after 400ms delay
          setTimeout(() => setIncomeBandVisible(true), 400);
        }
      } catch (e) {
        // Silently fail — income band is optional
        console.debug('Income band classification skipped:', e?.message);
      }
    };
    classifyProfile();
  }, [analysisData, profileText, selectedLanguage]);

  // Translate AI content when app language differs
  useEffect(() => {
    if (!analysisData || language === 'en') {
      setTranslatedContent(null);
      return;
    }
    const textsToTranslate = [];
    const keyMap = [];

    // Insight
    if (insight) { textsToTranslate.push(insight); keyMap.push('insight'); }

    // Scheme names, benefits, descriptions
    (schemes || []).forEach((s, i) => {
      if (s.scheme_name) { textsToTranslate.push(s.scheme_name); keyMap.push(`scheme_name_${i}`); }
      if (s.benefit_amount) { textsToTranslate.push(s.benefit_amount); keyMap.push(`scheme_benefit_${i}`); }
      if (s.why_you_qualify) { textsToTranslate.push(s.why_you_qualify); keyMap.push(`scheme_why_${i}`); }
      if (s.how_to_apply) { textsToTranslate.push(s.how_to_apply); keyMap.push(`scheme_how_${i}`); }
      if (s.effort_level) { textsToTranslate.push(s.effort_level); keyMap.push(`scheme_effort_${i}`); }
      if (s.apply_at) { textsToTranslate.push(s.apply_at); keyMap.push(`scheme_apply_${i}`); }
    });

    // Headwinds
    (headwinds || []).forEach((hw, i) => {
      if (hw.title) { textsToTranslate.push(hw.title); keyMap.push(`hw_title_${i}`); }
      if (hw.description) { textsToTranslate.push(hw.description); keyMap.push(`hw_desc_${i}`); }
    });

    // Action steps
    (action_steps || []).forEach((step, i) => {
      if (step.action) { textsToTranslate.push(step.action); keyMap.push(`step_action_${i}`); }
      if (step.what_to_bring) { textsToTranslate.push(step.what_to_bring); keyMap.push(`step_bring_${i}`); }
      if (step.where_to_go) { textsToTranslate.push(step.where_to_go); keyMap.push(`step_where_${i}`); }
    });

    // Deadlines
    (deadlines || []).forEach((dl, i) => {
      if (dl.scheme) { textsToTranslate.push(dl.scheme); keyMap.push(`dl_scheme_${i}`); }
      if (dl.deadline) { textsToTranslate.push(dl.deadline); keyMap.push(`dl_date_${i}`); }
    });

    // Benefit breakdown
    (benefit_breakdown || []).forEach((b, i) => {
      if (b.scheme) { textsToTranslate.push(b.scheme); keyMap.push(`bb_scheme_${i}`); }
      if (b.amount) { textsToTranslate.push(b.amount); keyMap.push(`bb_amount_${i}`); }
    });

    if (textsToTranslate.length === 0) return;

    // Batch translate in chunks of 50
    const translateAll = async () => {
      const result = {};
      for (let i = 0; i < textsToTranslate.length; i += 50) {
        const chunk = textsToTranslate.slice(i, i + 50);
        const chunkKeys = keyMap.slice(i, i + 50);
        try {
          const translated = await translateBatch(chunk, language);
          chunkKeys.forEach((key, idx) => { result[key] = translated[idx]; });
        } catch (e) { console.debug('ImpactResults translate chunk failed; keeping original', e?.message); }
      }
      setTranslatedContent(result);
    };
    translateAll();
  }, [analysisData, language]);

  if (!analysisData) return null;

  const tc = (key, fallback) => (translatedContent && translatedContent[key]) || fallback;

  const {
    summary, insight, schemes = [], total_benefit, benefit_breakdown = [],
    headwinds = [], deadlines = [], action_steps = [], csc_centres = [],
    suggested_questions = [], whatsapp_summary, news = [], profile
  } = analysisData;

  const handleFollowUp = async () => {
    if (!chatInput.trim()) return;
    const question = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', text: question }]);
    setChatLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/impact-analyser/follow-up`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question,
          context: {
            profile: analysisData.profile,
            policy_event: summary?.policy_event,
            schemes: schemes.map(s => s.scheme_name),
            total_benefit,
            // Link the follow-up to the originating analysis so admin sees
            // it grouped under that result (works for guests too).
            analysis_id: analysisData.analysis_id || null,
            analysis_created_at: analysisData.timestamp || null,
          },
          language: selectedLanguage,
          user_id: user?.id || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setChatMessages(prev => [...prev, { role: 'assistant', text: data.response }]);
      } else {
        setChatMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, I could not process your question. Please try again.' }]);
      }
    } catch {
      setChatMessages(prev => [...prev, { role: 'assistant', text: 'Connection error. Please try again.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleWhatsAppShare = () => {
    const text = whatsapp_summary || `Check out my personalized KMS Smart analysis on KnowMySchemes!`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleSuggestedQuestion = (q) => {
    setChatInput(q);
    setShowChat(true);
  };

  const severityColor = (s) => {
    if (s === 'high') return 'text-red-600 bg-red-50 border-red-200';
    if (s === 'medium') return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-blue-600 bg-blue-50 border-blue-200';
  };

  const urgencyColor = (u) => {
    if (u === 'urgent') return 'bg-red-100 text-red-700';
    if (u === 'soon') return 'bg-amber-100 text-amber-700';
    return 'bg-green-100 text-green-700';
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1 pb-20 md:pb-0">
        {/* Summary Bar */}
        <div className="bg-gradient-to-r from-green-700 to-teal-600 text-white">
          <div className="max-w-5xl mx-auto px-4 py-5 sm:py-6">
            <button
              data-testid="back-to-analyser"
              onClick={() => navigate('/impact-analyser')}
              className="flex items-center gap-1.5 text-green-200 hover:text-white text-sm mb-3 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> {ui.newAnalysis}
            </button>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2" data-testid="results-title">
                  <Sparkles className="w-5 h-5 text-yellow-300" />
                  {ui.yourReport}
                </h1>
                <p className="text-sm text-green-200 mt-1">
                  {summary?.policy_event} &middot; {summary?.date}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="share-whatsapp"
                  onClick={handleWhatsAppShare}
                  className="bg-white/15 hover:bg-white/25 text-white text-sm px-4 py-2 rounded-lg flex items-center gap-2"
                >
                  <Share2 className="w-4 h-4" /> {ui.share}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
          {/* 1. Personalized Insight */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6" data-testid="insight-section">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900 mb-2">{ui.personalizedInsight}</h2>
                <p className="text-sm text-gray-700 leading-relaxed">{tc('insight', insight)}</p>
              </div>
            </div>
          </section>

          {/* 2. Total Potential Benefit */}
          {total_benefit && (
            <section className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-200 p-6" data-testid="total-benefit-section">
              <div className="flex items-center gap-3 mb-4">
                <CircleDollarSign className="w-6 h-6 text-green-600" />
                <div>
                  <p className="text-xs text-green-600 font-medium uppercase tracking-wide">{ui.estimatedBenefit}</p>
                  <p className="text-2xl sm:text-3xl font-bold text-green-800">{total_benefit}</p>
                </div>
              </div>
              {benefit_breakdown.length > 0 && (
                <div className="space-y-2 mt-4">
                  {benefit_breakdown.map((b, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-medium text-gray-700 truncate">{tc(`bb_scheme_${i}`, b.scheme)}</span>
                          <span className="text-xs font-semibold text-green-700">{tc(`bb_amount_${i}`, b.amount)}</span>
                        </div>
                        <div className="w-full bg-green-200 rounded-full h-2">
                          <div className="bg-green-600 h-2 rounded-full transition-all" style={{ width: `${Math.min(b.percentage || 0, 100)}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* 3. Qualifying Schemes */}
          <section data-testid="schemes-section">
            <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-600" />
              {ui.qualifyingSchemes} ({schemes.length})
            </h2>
            <div className="space-y-3">
              {schemes.map((scheme, i) => {
                const isExpanded = expandedScheme === i;
                return (
                  <div
                    key={i}
                    data-testid={`scheme-card-${i}`}
                    className="bg-white rounded-xl border border-gray-200 overflow-hidden transition-shadow hover:shadow-md"
                  >
                    <button
                      className="w-full p-4 sm:p-5 text-left flex items-start gap-3"
                      onClick={() => setExpandedScheme(isExpanded ? null : i)}
                    >
                      <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center text-sm font-bold text-green-700">
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900">{tc(`scheme_name_${i}`, scheme.scheme_name)}</h3>
                            <p className="text-xs text-green-600 font-medium mt-0.5">{tc(`scheme_benefit_${i}`, scheme.benefit_amount)}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {scheme.confidence && (
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                scheme.confidence === 'high' ? 'bg-green-100 text-green-700' :
                                scheme.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {scheme.confidence}
                              </span>
                            )}
                            {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">{tc(`scheme_why_${i}`, scheme.why_you_qualify)}</p>
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="px-4 sm:px-5 pb-4 sm:pb-5 pt-0 border-t border-gray-100 space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3">
                          <InfoItem label={ui.effortLevel} value={tc(`scheme_effort_${i}`, scheme.effort_level)} />
                          <InfoItem label={ui.deadline} value={scheme.deadline} />
                          <InfoItem label={ui.whereToApply} value={tc(`scheme_apply_${i}`, scheme.apply_at)} />
                        </div>
                        {scheme.how_to_apply && (
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1">{ui.howToApply}</p>
                            <p className="text-xs text-gray-600 leading-relaxed">{tc(`scheme_how_${i}`, scheme.how_to_apply)}</p>
                          </div>
                        )}
                        {scheme.documents_needed?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1">{ui.documentsNeeded}</p>
                            <div className="flex flex-wrap gap-1.5">
                              {scheme.documents_needed.map((doc, j) => (
                                <span key={j} className="text-[11px] bg-gray-100 text-gray-600 px-2 py-1 rounded-md">{doc}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Official Apply link — curated static URL per scheme so users can act immediately */}
                        {(() => {
                          const link = getSchemeApplyLink(scheme);
                          if (!link) return null;
                          return (
                            <div className="pt-1">
                              <a
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`scheme-apply-link-${i}`}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-2 transition-colors shadow-sm"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                                {ui.applyOnPortal || 'Apply on official portal'}
                              </a>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* 3b. Contextual KMS Services — partner/service cards matched to the user */}
          <ContextualServices
            text={`${profileText} ${(schemes || []).map((s) => `${s.scheme_name || ''} ${s.sector || ''}`).join(' ')} ${summary?.policy_event || ''} ${profile?.occupation || ''} ${profile?.sector || ''}`}
          />

          {/* 4. Headwinds */}
          {headwinds.length > 0 && (
            <section data-testid="headwinds-section">
              <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-500" />
                {ui.headwinds}
              </h2>
              <div className="space-y-3">
                {headwinds.map((hw, i) => (
                  <div key={i} className={`rounded-xl border p-4 ${severityColor(hw.severity)}`} data-testid={`headwind-${i}`}>
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-sm font-semibold">{tc(`hw_title_${i}`, hw.title)}</h3>
                        <p className="text-xs mt-1 opacity-90">{tc(`hw_desc_${i}`, hw.description)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 5. Deadlines */}
          {deadlines.length > 0 && (
            <section data-testid="deadlines-section">
              <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-rose-500" />
                {ui.keyDeadlines}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {deadlines.map((dl, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${urgencyColor(dl.urgency)}`}>
                      {dl.urgency}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{tc(`dl_scheme_${i}`, dl.scheme)}</p>
                      <p className="text-xs text-gray-500">{tc(`dl_date_${i}`, dl.deadline)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 6. Action Steps */}
          {action_steps.length > 0 && (
            <section data-testid="action-steps-section">
              <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Footprints className="w-5 h-5 text-green-600" />
                {ui.nextSteps}
              </h2>
              <div className="space-y-3">
                {action_steps.map((step, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 flex gap-4" data-testid={`action-step-${i}`}>
                    <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {step.step_number}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{tc(`step_action_${i}`, step.action)}</p>
                      {step.what_to_bring && (
                        <p className="text-xs text-gray-500 mt-1">{ui.bring} {tc(`step_bring_${i}`, step.what_to_bring)}</p>
                      )}
                      {step.where_to_go && (
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                          <MapPin className="w-3 h-3" /> {tc(`step_where_${i}`, step.where_to_go)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 7. Nearby CSC Centres */}
          {csc_centres.length > 0 && (
            <section data-testid="csc-section">
              <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Navigation className="w-5 h-5 text-blue-600" />
                {ui.nearbyCentres}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {csc_centres.map((csc, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
                    <h3 className="text-sm font-semibold text-gray-900">{csc.name}</h3>
                    <p className="text-xs text-gray-500 mt-1">{csc.address}</p>
                    <p className="text-xs text-blue-600 mt-1">{csc.distance} {ui.away}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 8. Related News */}
          {news.length > 0 && (
            <section data-testid="news-section">
              <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Newspaper className="w-5 h-5 text-indigo-600" />
                {ui.relatedNews}
              </h2>
              <div className="space-y-2">
                {news.map((n, i) => (
                  <a
                    key={i}
                    href={n.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-white rounded-xl border border-gray-200 p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900 line-clamp-2">{n.title}</p>
                        <p className="text-xs text-gray-500 mt-1">{n.source} &middot; {n.pubDate}</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}

          {/* 9. Suggested Questions */}
          {suggested_questions.length > 0 && (
            <section data-testid="suggested-questions-section">
              <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-green-600" />
                {ui.moreQuestions}
              </h2>
              <div className="flex flex-wrap gap-2">
                {suggested_questions.map((q, i) => (
                  <button
                    key={i}
                    data-testid={`suggested-question-${i}`}
                    onClick={() => handleSuggestedQuestion(q)}
                    className="text-xs bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 px-3 py-2 rounded-full transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* 10. Income Intelligence */}
          {incomeBandData && (
            <section
              data-testid="income-intelligence-section"
              className="transition-all duration-300 ease-out"
              style={{
                opacity: incomeBandVisible ? 1 : 0,
                transform: incomeBandVisible ? 'translateY(0)' : 'translateY(8px)',
              }}
            >
              <div
                className="rounded-2xl border p-5 sm:p-6"
                style={{
                  backgroundColor: incomeBandData.colors?.bg || '#EAF3DE',
                  borderColor: incomeBandData.colors?.border || '#C0DD97',
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" style={{ color: incomeBandData.colors?.text || '#4A7C10' }} />
                    <h2 className="text-base font-semibold" style={{ color: incomeBandData.colors?.text }}>
                      {ui.incomeIntelligence}
                    </h2>
                  </div>
                  <span
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-full text-white flex items-center gap-1"
                    style={{ backgroundColor: incomeBandData.colors?.border }}
                    data-testid="smart-tip-badge"
                  >
                    <TrendingUp className="w-3 h-3" />
                    {ui.smartTip}
                  </span>
                </div>

                <p
                  className="text-sm font-medium mb-4 leading-relaxed"
                  style={{ color: incomeBandData.colors?.text }}
                  data-testid="income-band-header"
                >
                  {incomeBandData.header}
                </p>

                <div className="space-y-3 mb-4">
                  {incomeBandData.opportunities?.map((opp, i) => (
                    <div key={i} className="flex items-center gap-3" data-testid={`income-opp-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-700">{opp.name}</span>
                          <span
                            className="text-xs font-bold flex-shrink-0 ml-2"
                            style={{ color: incomeBandData.colors?.text }}
                          >
                            {opp.value}
                          </span>
                        </div>
                        <div className="w-full bg-white/60 rounded-full h-1.5">
                          <div
                            className="h-1.5 rounded-full transition-all duration-500"
                            style={{
                              width: `${opp.bar_pct || 50}%`,
                              backgroundColor: incomeBandData.colors?.border,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  data-testid="income-band-cta"
                  onClick={() => navigate(incomeBandData.cta_link || '/income-intelligence')}
                  className="flex items-center gap-1.5 text-sm font-semibold transition-opacity hover:opacity-80"
                  style={{ color: incomeBandData.colors?.text }}
                >
                  {ui.seeFullPlan}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </section>
          )}

          {/* 11. Follow-up Chat */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid="chat-section">
            <button
              data-testid="toggle-chat"
              onClick={() => setShowChat(!showChat)}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">{ui.askFollowUp}</h2>
                  <p className="text-xs text-gray-500">{ui.chatSubtitle}</p>
                </div>
              </div>
              {showChat ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>
            {showChat && (
              <div className="border-t border-gray-100">
                {/* Chat Messages */}
                <div className="max-h-64 overflow-y-auto p-4 space-y-3">
                  {chatMessages.length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-4">{ui.chatPlaceholder}</p>
                  )}
                  {chatMessages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${
                        msg.role === 'user'
                          ? 'bg-green-600 text-white rounded-br-sm'
                          : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                      }`}>
                        {msg.text}
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 rounded-xl px-4 py-2 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-green-600" />
                        <span className="text-xs text-gray-500">{ui.thinking}</span>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
                {/* Chat Input */}
                <div className="p-3 border-t border-gray-100 flex gap-2">
                  <input
                    data-testid="chat-input"
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !chatLoading && handleFollowUp()}
                    placeholder={ui.typePlaceholder}
                    className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <Button
                    data-testid="send-chat"
                    onClick={handleFollowUp}
                    disabled={chatLoading || !chatInput.trim()}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg disabled:opacity-50"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </section>

          {/* New Analysis Button */}
          <div className="text-center pt-4">
            <Button
              data-testid="new-analysis-btn"
              onClick={() => navigate('/impact-analyser')}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl text-sm font-semibold"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {ui.startNewAnalysis}
            </Button>
          </div>
        </div>
      </main>
      <div className="hidden md:block">
        <Footer />
      </div>
    </div>
  );
}

function InfoItem({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">{label}</p>
      <p className="text-xs text-gray-700 mt-0.5">{value}</p>
    </div>
  );
}
